import { useState } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { User, X, Search, UserCheck } from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const seatColors = {
    available: 'bg-emerald-500/20 border-emerald-500/40 hover:bg-emerald-500/40 cursor-pointer',
    occupied: 'bg-sky-500/20 border-sky-500/40 cursor-pointer',
    checked_in: 'bg-sky-500/20 border-sky-500/40 cursor-pointer',
    blocked: 'bg-zinc-800/50 border-zinc-700/30 cursor-not-allowed opacity-40',
    selected: 'bg-primary/30 border-primary ring-2 ring-primary/50',
};

export function SeatMap({ seatData, flightId, passengers = [], onUpdate, readOnly = false }) {
    const [selectedSeat, setSelectedSeat] = useState(null);
    const [assignDialog, setAssignDialog] = useState(null);
    const [unassignDialog, setUnassignDialog] = useState(null);
    const [paxSearch, setPaxSearch] = useState('');

    if (!seatData || !seatData.has_seat_map) {
        return (
            <div className="text-center py-8 text-muted-foreground">
                <p>Sin mapa de asientos configurado</p>
                <p className="text-xs mt-1">Configure los asientos en el modulo de Flota</p>
            </div>
        );
    }

    const { rows, seat_config: config, sections, stats, aircraft_registration, aircraft_type } = seatData;

    // Passengers without seat (pending check-in, or checked-in without seat from older data)
    const pendingPax = passengers.filter(p => !p.seat);
    // Passengers checked-in with seat
    const checkedInPax = passengers.filter(p => p.checked_in);

    const handleSeatClick = (seat) => {
        if (readOnly || seat.status === 'blocked') return;
        if (seat.status === 'occupied') {
            setUnassignDialog(seat);
        } else {
            setSelectedSeat(seat.code);
            setAssignDialog(seat);
            setPaxSearch('');
        }
    };

    const assignSeatToPassenger = async (pax) => {
        if (!assignDialog) return;
        try {
            // Check-in + assign seat in one action
            await axios.patch(`${API}/counter/flights/${flightId}/passengers/${pax.id}/checkin`, {
                seat: assignDialog.code,
            });
            toast.success(`${pax.first_name} ${pax.last_name} → Asiento ${assignDialog.code} — Check-in completado`);
            setAssignDialog(null);
            setSelectedSeat(null);
            onUpdate?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al asignar'); }
    };

    const unassignSeat = async () => {
        if (!unassignDialog) return;
        try {
            await axios.delete(`${API}/flights/${flightId}/seats/${unassignDialog.code}`);
            // Also update passenger seat field
            const pax = passengers.find(p => p.seat === unassignDialog.code);
            if (pax) {
                await axios.put(`${API}/counter/flights/${flightId}/passengers/${pax.id}`, {
                    seat: '', checked_in: false, checked_in_at: null,
                });
            }
            toast.success(`Asiento ${unassignDialog.code} liberado`);
            setUnassignDialog(null);
            onUpdate?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    };

    const filteredPax = pendingPax.filter(p =>
        `${p.first_name} ${p.last_name}`.toLowerCase().includes(paxSearch.toLowerCase()) ||
        (p.document_number || '').toLowerCase().includes(paxSearch.toLowerCase())
    );

    // Section headers
    const sectionForRow = {};
    (sections || []).forEach(s => {
        for (let r = s.start_row; r <= s.end_row; r++) sectionForRow[r] = s.name;
    });
    let lastSection = '';

    const allCols = [...(config.columns_left || []), ...(config.columns_right || [])];

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="font-mono font-bold text-primary">{aircraft_registration} <span className="text-muted-foreground font-normal">({aircraft_type})</span></p>
                <div className="flex items-center gap-3 text-[10px]">
                    <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-emerald-500/30 border border-emerald-500/40" /><span>Disponible ({stats.available})</span></div>
                    <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-sky-500/30 border border-sky-500/40" /><span>Ocupado ({stats.occupied})</span></div>
                    <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-zinc-800/50 border border-zinc-700/30" /><span>Bloqueado</span></div>
                    <Badge variant="outline" className="font-mono">{stats.pct}%</Badge>
                </div>
            </div>

            {/* Pending check-in banner */}
            {pendingPax.length > 0 && (
                <div className="text-xs bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 flex items-center gap-2">
                    <UserCheck className="w-3.5 h-3.5 text-amber-400" />
                    <span><strong>{pendingPax.length}</strong> pasajero(s) pendientes de check-in — Click en un asiento para asignar</span>
                </div>
            )}

            {/* Fuselage */}
            <div className="relative mx-auto" style={{ maxWidth: `${(allCols.length * 44) + 60}px` }}>
                <div className="mx-auto w-16 h-6 bg-gradient-to-b from-muted/80 to-transparent rounded-t-full mb-1" />
                <div className="flex items-center justify-center gap-0.5 mb-1 px-6">
                    {config.columns_left.map(c => <div key={`h-l-${c}`} className="w-9 h-5 flex items-center justify-center text-[10px] font-bold text-muted-foreground">{c}</div>)}
                    <div className="w-6" />
                    {config.columns_right.map(c => <div key={`h-r-${c}`} className="w-9 h-5 flex items-center justify-center text-[10px] font-bold text-muted-foreground">{c}</div>)}
                </div>
                <div className="border-l-2 border-r-2 border-muted/40 rounded-lg px-1 py-1 bg-muted/5" data-testid="seat-map-body">
                    {rows.map(row => {
                        const section = sectionForRow[row.row];
                        const showHeader = section && section !== lastSection;
                        if (showHeader) lastSection = section;
                        const leftSeats = row.seats.filter(s => s.side === 'left');
                        const rightSeats = row.seats.filter(s => s.side === 'right');
                        return (
                            <div key={row.row}>
                                {showHeader && (
                                    <div className="flex items-center gap-2 my-1.5">
                                        <div className="h-px flex-1 bg-primary/20" /><span className="text-[9px] font-bold text-primary uppercase tracking-widest">{section}</span><div className="h-px flex-1 bg-primary/20" />
                                    </div>
                                )}
                                <div className="flex items-center justify-center gap-0.5 mb-0.5">
                                    <div className="w-5 text-right pr-0.5"><span className={`text-[9px] font-mono ${row.seats[0]?.is_exit_row ? 'text-red-400 font-bold' : 'text-muted-foreground'}`}>{row.row}</span></div>
                                    {leftSeats.map(seat => {
                                        const pax = seat.passenger;
                                        return (
                                            <button key={seat.code} className={`w-9 h-7 rounded border text-[10px] font-mono font-bold transition-all ${selectedSeat === seat.code ? seatColors.selected : seatColors[seat.status]}`}
                                                onClick={() => handleSeatClick(seat)} disabled={seat.status === 'blocked'}
                                                title={pax ? `${pax.passenger_name} (${seat.code})` : seat.code}
                                                data-testid={`seat-${seat.code}`}>
                                                {seat.status === 'occupied' ? <User className="w-3 h-3 mx-auto" /> : seat.status === 'blocked' ? <X className="w-3 h-3 mx-auto opacity-30" /> : seat.col}
                                            </button>
                                        );
                                    })}
                                    <div className="w-6" />
                                    {rightSeats.map(seat => {
                                        const pax = seat.passenger;
                                        return (
                                            <button key={seat.code} className={`w-9 h-7 rounded border text-[10px] font-mono font-bold transition-all ${selectedSeat === seat.code ? seatColors.selected : seatColors[seat.status]}`}
                                                onClick={() => handleSeatClick(seat)} disabled={seat.status === 'blocked'}
                                                title={pax ? `${pax.passenger_name} (${seat.code})` : seat.code}
                                                data-testid={`seat-${seat.code}`}>
                                                {seat.status === 'occupied' ? <User className="w-3 h-3 mx-auto" /> : seat.status === 'blocked' ? <X className="w-3 h-3 mx-auto opacity-30" /> : seat.col}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="mx-auto w-10 h-4 bg-gradient-to-t from-muted/80 to-transparent rounded-b-full mt-1" />
            </div>

            {/* Assign Dialog — Select passenger from pending list */}
            <Dialog open={!!assignDialog} onOpenChange={() => { setAssignDialog(null); setSelectedSeat(null); }}>
                <DialogContent className="sm:max-w-[420px]" data-testid="assign-seat-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">
                            Check-in → Asiento {assignDialog?.code}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        {pendingPax.length === 0 ? (
                            <div className="text-center py-6 text-muted-foreground">
                                <UserCheck className="w-8 h-8 mx-auto mb-2 opacity-40" />
                                <p className="text-sm">Todos los pasajeros ya tienen check-in</p>
                                <p className="text-xs mt-1">Agrega pasajeros desde la tab "Pasajeros"</p>
                            </div>
                        ) : (
                            <>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input placeholder="Buscar pasajero..." value={paxSearch} onChange={e => setPaxSearch(e.target.value)} className="pl-10" data-testid="assign-pax-search" />
                                </div>
                                <p className="text-xs text-muted-foreground">{filteredPax.length} pasajero(s) pendientes de check-in</p>
                                <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                                    {filteredPax.map(p => (
                                        <button key={p.id} className="w-full text-left p-2.5 rounded-lg border border-border/50 hover:border-primary/40 hover:bg-primary/5 transition-all flex items-center justify-between gap-2"
                                                onClick={() => assignSeatToPassenger(p)} data-testid={`assign-pax-${p.id}`}>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium truncate">{p.first_name} {p.last_name}</p>
                                                <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-0.5">
                                                    <span>{p.document_type}: {p.document_number}</span>
                                                    <Badge variant="outline" className="text-[9px] h-4">{p.pax_type === 'adult' ? 'ADT' : p.pax_type === 'child' ? 'CHD' : 'INF'}</Badge>
                                                </div>
                                            </div>
                                            <Badge className="bg-primary/10 text-primary text-[10px]">Asignar {assignDialog?.code}</Badge>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Unassign / View Dialog */}
            <Dialog open={!!unassignDialog} onOpenChange={() => setUnassignDialog(null)}>
                <DialogContent className="sm:max-w-[350px]" data-testid="unassign-seat-dialog">
                    <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">Asiento {unassignDialog?.code}</DialogTitle></DialogHeader>
                    {unassignDialog?.passenger && (
                        <div className="p-3 bg-muted/30 rounded-lg">
                            <p className="font-bold text-sm">{unassignDialog.passenger.passenger_name}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{unassignDialog.passenger.passenger_doc} | {unassignDialog.passenger.ticket_number}</p>
                            <Badge variant="outline" className="mt-2 text-[10px]">{unassignDialog.passenger.pax_type === 'adult' ? 'Adulto' : unassignDialog.passenger.pax_type === 'child' ? 'Nino' : 'Infante'}</Badge>
                        </div>
                    )}
                    {!readOnly && (
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setUnassignDialog(null)}>Cerrar</Button>
                            <Button variant="destructive" onClick={unassignSeat} data-testid="confirm-unassign"><X className="w-4 h-4 mr-1" />Liberar Asiento</Button>
                        </DialogFooter>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
